/**
 * DeploymentDetail Page
 *
 * Shows detailed information about a specific deployment including approval status.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tokens } from '../local-components';
import { useAgent, type ApprovalRequest } from '../hooks/useAgent';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface DeploymentHost {
  id: string;
  status: string;
  health: string | null;
}

interface DeploymentData {
  id: string;
  artifact: string;
  version: string;
  environment: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  error: string | null;
  strategy: string | null;
  hosts: DeploymentHost[];
  logs: string[];
}

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
};

const backLinkStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.mint,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.spacing[1],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const versionStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  color: tokens.colors.textMuted,
  marginLeft: tokens.spacing[3],
};

const sectionStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[5],
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[4],
};

const sectionTitleStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  margin: 0,
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: tokens.spacing[4],
};

const infoItemStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[1],
};

const labelStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const valueStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
};

const approvalCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[3],
};

const commentStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
  marginTop: tokens.spacing[2],
  paddingTop: tokens.spacing[3],
  borderTop: `1px solid ${tokens.colors.border}`,
};

const errorSectionStyle: CSSProperties = {
  ...sectionStyle,
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
};

const errorPreStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

const hostsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: tokens.spacing[3],
};

const hostCardStyle: CSSProperties = {
  backgroundColor: tokens.colors.void,
  borderRadius: tokens.radii.md,
  padding: tokens.spacing[3],
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
};

const emptyStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const notFoundStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[8],
  textAlign: 'center',
};

// ═══════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={infoItemStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const getColor = (s: string): string => {
    switch (s) {
      case 'succeeded':
        return tokens.colors.success;
      case 'pending':
      case 'running':
        return tokens.colors.warning;
      case 'failed':
      case 'cancelled':
        return tokens.colors.danger;
      case 'rolled_back':
        return '#aa88ff';
      default:
        return tokens.colors.textMuted;
    }
  };

  const badgeStyle: CSSProperties = {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    fontWeight: tokens.fontWeights.semibold,
    color: getColor(status),
    backgroundColor: `${getColor(status)}20`,
    padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
    borderRadius: tokens.radii.sm,
    textTransform: 'uppercase',
  };

  return (
    <span style={badgeStyle} data-testid="status-badge" data-status={status}>
      {status}
    </span>
  );
}

function ApprovalStatusBadge({ status }: { status: ApprovalRequest['status'] }) {
  const getColor = (s: ApprovalRequest['status']): string => {
    switch (s) {
      case 'approved':
        return tokens.colors.success;
      case 'pending':
        return tokens.colors.warning;
      case 'rejected':
        return tokens.colors.danger;
      case 'expired':
      case 'cancelled':
        return tokens.colors.textMuted;
    }
    return tokens.colors.textMuted;
  };

  const badgeStyle: CSSProperties = {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    fontWeight: tokens.fontWeights.semibold,
    color: getColor(status),
    backgroundColor: `${getColor(status)}20`,
    padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
    borderRadius: tokens.radii.sm,
    textTransform: 'uppercase',
  };

  return <span style={badgeStyle}>{status}</span>;
}

function formatTime(timestamp: string | null): string {
  if (timestamp === null) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString();
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function DeploymentDetail() {
  const { id } = useParams<{ id: string }>();
  const { isConnected, listApprovals, listDeployments } = useAgent();

  const [deployment, setDeployment] = useState<DeploymentData | null>(null);
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isConnected || id === undefined) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch deployments and find the matching one
        const deployments = await listDeployments();
        const found = deployments.find((d) => d.id === id) ?? null;

        if (found !== null) {
          // Convert to DeploymentData format (with additional fields as placeholders)
          setDeployment({
            id: found.id,
            artifact: found.artifact,
            version: found.version,
            environment: found.environment,
            status: found.status,
            startedAt: found.startedAt,
            completedAt: found.completedAt,
            error: found.error,
            strategy: null, // Not available from current API
            hosts: [], // Not available from current API
            logs: [], // Not available from current API
          });
        } else {
          // Deployment not found in list, create mock placeholder
          setDeployment({
            id,
            artifact: 'unknown',
            version: 'unknown',
            environment: 'unknown',
            status: 'unknown',
            startedAt: new Date().toISOString(),
            completedAt: null,
            error: null,
            strategy: null,
            hosts: [],
            logs: [],
          });
        }

        // Fetch related approval (if any)
        const approvals = await listApprovals({
          status: null,
          environment: null,
          artifact: null,
          limit: 100,
        });
        // Find approval matching this deployment ID
        const related = approvals.find((a) => a.deploymentId === id) ?? null;
        setApproval(related);
      } catch (err) {
        console.error('Failed to fetch deployment data:', err);
        // Set placeholder on error
        setDeployment({
          id: id ?? 'unknown',
          artifact: 'unknown',
          version: 'unknown',
          environment: 'unknown',
          status: 'unknown',
          startedAt: new Date().toISOString(),
          completedAt: null,
          error: 'Failed to fetch deployment data',
          strategy: null,
          hosts: [],
          logs: [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isConnected, id, listApprovals, listDeployments]);

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <Link to="/deployments" style={backLinkStyle}>
          &larr; Back to Deployments
        </Link>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <Link to="/deployments" style={backLinkStyle}>
          &larr; Back to Deployments
        </Link>
        <div style={loadingStyle}>Loading deployment details...</div>
      </div>
    );
  }

  if (deployment === null) {
    return (
      <div style={pageStyle}>
        <Link to="/deployments" style={backLinkStyle}>
          &larr; Back to Deployments
        </Link>
        <div style={notFoundStyle}>Deployment not found</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Back Link */}
      <Link to="/deployments" style={backLinkStyle}>
        &larr; Back to Deployments
      </Link>

      {/* Header */}
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <h1 style={titleStyle}>{deployment.artifact}</h1>
          <span style={versionStyle}>{deployment.version}</span>
        </div>
        <StatusBadge status={deployment.status} />
      </header>

      {/* Details Section */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Details</h2>
        <div style={gridStyle}>
          <InfoItem label="Environment" value={deployment.environment} />
          <InfoItem label="Strategy" value={deployment.strategy ?? 'sequential'} />
          <InfoItem label="Started" value={formatTime(deployment.startedAt)} />
          <InfoItem label="Completed" value={formatTime(deployment.completedAt)} />
        </div>
      </section>

      {/* Approval Section */}
      {approval !== null && (
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Approval</h2>
          <div style={approvalCardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span
                style={{
                  fontFamily: tokens.fonts.mono,
                  fontSize: tokens.fontSizes.sm,
                  color: tokens.colors.textMuted,
                }}
              >
                Status
              </span>
              <ApprovalStatusBadge status={approval.status} />
            </div>
            <div style={gridStyle}>
              <InfoItem label="Requested By" value={approval.requestedBy} />
              <InfoItem label="Created" value={formatTime(approval.createdAt)} />
              {approval.reviewedBy !== null && (
                <>
                  <InfoItem label="Reviewed By" value={approval.reviewedBy} />
                  <InfoItem label="Reviewed At" value={formatTime(approval.reviewedAt)} />
                </>
              )}
            </div>
            {approval.reviewComment !== null && (
              <div style={commentStyle}>
                <span style={labelStyle}>Comment: </span>
                {approval.reviewComment}
              </div>
            )}
            {approval.reason !== null && (
              <div style={commentStyle}>
                <span style={labelStyle}>Reason: </span>
                {approval.reason}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Error Section */}
      {deployment.error !== null && (
        <section style={errorSectionStyle}>
          <h2 style={sectionTitleStyle}>Error</h2>
          <pre style={errorPreStyle}>{deployment.error}</pre>
        </section>
      )}

      {/* Hosts Section */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Hosts</h2>
        {deployment.hosts.length === 0 ? (
          <p style={emptyStyle}>No host information available</p>
        ) : (
          <div style={hostsGridStyle}>
            {deployment.hosts.map((host) => (
              <div key={host.id} style={hostCardStyle}>
                <span>{host.id}</span>
                <StatusBadge status={host.status} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Logs Section Placeholder */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Logs</h2>
        {deployment.logs.length === 0 ? (
          <p style={emptyStyle}>No logs available. View detailed logs in Grafana.</p>
        ) : (
          <pre
            style={{
              fontFamily: tokens.fonts.mono,
              fontSize: tokens.fontSizes.xs,
              color: tokens.colors.textPrimary,
              backgroundColor: tokens.colors.void,
              padding: tokens.spacing[4],
              borderRadius: tokens.radii.md,
              overflow: 'auto',
              maxHeight: '300px',
              margin: 0,
            }}
          >
            {deployment.logs.join('\n')}
          </pre>
        )}
      </section>
    </div>
  );
}
